import React from 'react'
import clsx from 'clsx'
import { Clock, CloseCircle } from 'iconsax-react'
import { useTheme } from '@mui/material'

import Spacer from '../../../atoms/Spacer'
import NewCount from '../../BaseConversation/NewCount'
import TypingIndicator from '../../../atoms/TypingIndicator'
import Tooltip from '../../../atoms/Tooltip'

import {
  getShortenedDistance,
  formatLastMessage,
  loadTaggedUsers
} from '../../../../utilities'
import { useAgent } from '../../../../providers/AgentProvider'
import { useUserAPI } from '../../../../providers/UserAPIProvider'
import { useReadStatus } from '../../../../providers/ReadStatusProvider'
import useInterval from '../../../../hooks/useInterval'
import { useQueueState } from '../../../../providers/QueueStateProvider'
import { useNotificationCount } from '../../../../providers/NotificationCountProvider'
import useStyles from '../../BaseConversation/Content/styles'

const DELETED = 'Deleted Contact'

const Content = ({
  name,
  selected,
  lastMessage,
  hovering,
  isNew,
  typingNames,
  numUsers,
  handleCloseConversation,
  users,
  related,
  ownerID
}) => {
  const { allUsers, loadUser, getUsers } = useUserAPI()
  const { currentUser } = useAgent()
  const { convos, wholeConvos, setWholeConvos } = useReadStatus()
  const { id: queueSelected } = useQueueState()
  const { updateCount } = useNotificationCount()
  const theme = useTheme()
  const [loadedUsers, setLoadedUsers] = React.useState(null)
  const [names, setNames] = React.useState('loading...')
  const [lastLoaded, setLastLoaded] = React.useState('')

  const newCount = React.useMemo(() => {
    const totals = related.map(convo => {
      const { session, messages } = convo

      if (session === queueSelected) {
        return 0
      }

      return updateCount(session, convos, messages)
    })

    return totals.reduce((prev, curr) => prev + curr, 0)
  }, [related, queueSelected, updateCount, convos])

  const allAgents = React.useMemo(
    () => Object.values(getUsers(false)),
    [getUsers]
  )

  // Get stored user info
  const user = allUsers[ownerID]

  // Clears notifications
  React.useEffect(() => {
    related.forEach(convo => {
      const { session } = convo

      if (session === queueSelected && !wholeConvos[session]?.cleared) {
        setWholeConvos(convos => ({
          ...convos,
          [session]: {
            ...convos[session],
            unread: 0,
            cleared: true
          }
        }))
      }
    })
  }, [related, queueSelected, wholeConvos, setWholeConvos])

  const classes = useStyles()

  React.useEffect(() => {
    if (name) {
      return
    }

    if (allUsers) {
      setLoadedUsers(true)
    }
    // load in the names array
    if (!name && Object.keys(allUsers).length > 0) {
      const namesArray = []
      if (users.length === 1) {
        setNames([currentUser()?.name])
      } else {
        users.forEach(id => {
          if (id !== currentUser()?.id) {
            if (!allUsers[id]) {
              loadUser(id)
            } else {
              const user = allUsers[id]
              if (user.deleted_at) {
                namesArray.push(
                  <span className={classes.faded}>{DELETED}</span>
                )
              } else {
                namesArray.push(user.name)
              }
            }
          }
        })
        setNames(namesArray)
      }
    }
  }, [users, allUsers, currentUser, loadedUsers, name, loadUser, classes.faded])

  const [timeSince, setTimeSince] = React.useState('')
  const nameRef = React.useRef()

  // Updates time since every 5 seconds
  const updateTimeSince = React.useCallback(() => {
    setTimeSince(getShortenedDistance(lastMessage?.date))
  }, [lastMessage])
  useInterval(1000 * 5, updateTimeSince)

  const lastText = formatLastMessage(
    lastMessage,
    allAgents,
    allUsers,
    currentUser
  )

  // Loads all tagged users from a message
  React.useEffect(() => {
    if (
      !lastMessage ||
      lastMessage.content === lastLoaded ||
      typeof lastMessage.content !== 'string'
    ) {
      return
    }

    const { content = '' } = lastMessage
    loadTaggedUsers(content, loadUser)

    setLastLoaded(content)
  }, [lastMessage, lastLoaded, loadUser])

  const iconElem =
    selected || (!selected && hovering) ? (
      <CloseCircle
        size={theme.size.base}
        onClick={e => {
          e.stopPropagation()
          handleCloseConversation()
        }}
        className='icon button'
      />
    ) : (
      <Clock size={theme.size.base} className='icon' />
    )

  let nameElem = (
    <div
      ref={nameRef}
      className={clsx(classes.name, user?.deleted_at && classes.faded)}
    >
      {user?.deleted_at ? DELETED : name}
    </div>
  )

  // Show tooltip if name is overflowing
  if (nameRef.current) {
    const element = nameRef.current
    if (element.offsetWidth < element.scrollWidth) {
      nameElem = (
        <Tooltip
          title={user?.deleted_at ? DELETED : name}
          placement='right'
          arrow
        >
          {nameElem ?? 'Loading...'}
        </Tooltip>
      )
    }
  }

  return (
    <div className={classes.root}>
      <div className={classes.contentBlock}>
        <div className={classes.title}>
          {name ? (
            <div className={classes.name}>{nameElem}</div>
          ) : (
            <div className={classes.name}>
              {Array.isArray(names)
                ? names.map((name, i) => (
                    <React.Fragment key={i}>
                      {name}
                      {i !== names.length - 1 && ', '}
                    </React.Fragment>
                  ))
                : names}
            </div>
          )}

          <Spacer />

          <div className={classes.time}>{timeSince}</div>
        </div>

        <div className={classes.content}>
          {typingNames.length > 0 ? (
            <TypingIndicator names={typingNames} numUsers={numUsers} />
          ) : (
            lastText
          )}
        </div>
      </div>

      <div className={classes.controlsBlock}>
        {iconElem}

        {lastMessage && lastMessage.senderFull?.id !== currentUser()?.id ? (
          isNew || newCount ? (
            <NewCount count={newCount} />
          ) : (
            <div />
          )
        ) : (
          <div />
        )}
      </div>
    </div>
  )
}

export default Content
